import { useState } from 'react';
import { login, ApiError, type User } from '../api.ts';

/**
 * Sign-in.
 *
 * The server deliberately answers "invalid credentials" identically for an unknown user
 * and a wrong password, and equalizes the timing, so this screen must not undo that by
 * distinguishing them in the copy.
 */

/**
 * What actually went wrong, rather than what usually goes wrong.
 *
 * This used to end in a bare `: 'Invalid username or password.'`, which meant EVERY
 * failure that was not a known code said the password was bad - a dropped connection, a
 * 500, a proxy with nothing behind it, a dev server caught mid-restart. It cost real
 * time: the API here runs under `--watch` and restarts on every file save, so while
 * several ingests were writing to the repo the sign-in flapped, and the screen blamed
 * the credentials. They were correct the whole time and the server had recorded every
 * attempt as successful.
 *
 * The rule this now follows: only say the password is wrong when the SERVER said so.
 * Anything else is reported as what it is, because a message that names the wrong cause
 * is worse than a vague one - it sends the reader off to fix something that is not
 * broken.
 *
 * The one distinction still deliberately NOT made is between an unknown user and a wrong
 * password. That is the server's design and the copy has to preserve it.
 */
function signInError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'too_many_attempts':
        return 'Too many attempts from this address. Wait a few minutes and try again.';
      case 'account_locked':
        return 'This account is temporarily locked after repeated failed attempts.';
      case 'invalid_credentials':
        return 'Invalid username or password.';
    }
    // A sentence the server wrote for whoever is reading it beats anything guessed here.
    if (err.detail) return err.detail;
    // A 5xx is the server's fault and the reader can do nothing about it except retry.
    // Saying so is the difference between waiting and changing your password.
    if (err.status >= 500) {
      return `The server hit an error (${err.status}). Nothing is wrong with your details - `
           + 'try again in a moment.';
    }
    return `Sign-in failed (${err.status} ${err.code}).`;
  }
  // fetch() rejects rather than resolving when the request never completed - the server
  // is down, restarting, or the dev proxy has nothing behind it. This is the case that
  // was being reported as a bad password.
  if (err instanceof TypeError) {
    return 'Cannot reach the server. It may be restarting - try again in a few seconds.';
  }
  return 'Sign-in failed for an unknown reason. Check the console for details.';
}
export default function Login({ onSignedIn, onCancel }: {
  onSignedIn: (u: User) => void;
  /**
   * Back to the public desk. Staff reach this form from "Staff sign in" now - brokers are
   * asked for a phone at Search instead - so it needs a way back out that is not the
   * browser's back button, which would leave the site.
   */
  onCancel?: () => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { user } = await login(username, password);
      onSignedIn(user);
    } catch (err) {
      setError(signInError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      minHeight: '100%', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 20,
    }}>
      <form onSubmit={submit} className="card" style={{ width: 380 }}>
        {/* The icon sits inside the h1 with alt="", so the heading's accessible name is
            still exactly "LenderHound" rather than the name said twice. */}
        <h1 style={{
          margin: '0 0 4px', fontSize: 30, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <img src="/logo.png" alt="" width={48} height={48}
               style={{ display: 'block', flexShrink: 0 }} />
          LenderHound
        </h1>
        <p style={{ margin: '0 0 24px', color: 'var(--muted)', fontSize: 14 }}>
          Staff sign in. Brokers need no account - press Search on the desk.
        </p>
        <label className="lbl" htmlFor="username">Username</label>
        <input id="username" className="finput" autoComplete="username" autoFocus
               value={username} onChange={(e) => setUsername(e.target.value)} />
        <label className="lbl" htmlFor="password" style={{ marginTop: 14 }}>Password</label>
        <input id="password" className="finput" type="password" autoComplete="current-password"
               value={password} onChange={(e) => setPassword(e.target.value)} />
        {error ? <div className="err-text" style={{ marginTop: 10 }}>{error}</div> : null}
        <button type="submit" className="btn btn-primary"
                style={{ width: '100%', marginTop: 18 }}
                disabled={busy || !username || !password}>
          {busy ? <><span className="spin" /> &nbsp;Signing in…</> : 'Sign In'}
        </button>
        {onCancel ? (
          <button type="button" className="btn btn-ghost"
                  style={{ width: '100%', marginTop: 10 }} onClick={onCancel}>
            ← Back to the Scenario Desk
          </button>
        ) : null}
      </form>
    </div>
  );
}
